import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { LoggerService } from '@core/services/logger.service';
import { sanitizeUrl } from '@core/utils/url-sanitizer';

/**
 * Dev Tools Guard - Runtime Security Protection
 *
 * **Purpose**: Prevent access to development-only routes in production builds
 *
 * **Why Runtime Check is Critical**:
 * - Build-time route filtering in app.routes.ts can fail if environment flag is misconfigured
 * - This guard provides defense-in-depth by checking at runtime
 * - Protects against accidental exposure of test-backend and style-guide routes
 *
 * **Security Risk Without This Guard**:
 * - If environment.features.enableDevTools is accidentally set to true in production
 * - Or if production build uses wrong environment file
 * - Dev routes (/test-backend, /style-guide) would be accessible
 * - Exposing internal system architecture and diagnostic tools
 *
 * **Implementation**:
 * - Checks environment.features.enableDevTools at runtime
 * - Checks environment.production flag as secondary verification
 * - Redirects unauthorized access to /unauthorized page
 * - Logs security violation attempts for monitoring
 *
 * **Usage**:
 * ```typescript
 * {
 *   path: 'test-backend',
 *   canActivate: [devToolsGuard],
 *   loadComponent: () => import('./features/test-backend/test-backend.component')
 * }
 * ```
 */
export const devToolsGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const logger = inject(LoggerService);

  // Runtime check: Verify dev tools are enabled
  const isDevToolsEnabled = environment.features?.enableDevTools === true;
  const isProduction = environment.production === true;

  // Defense-in-depth: Block access if either:
  // 1. Dev tools flag is false (expected in production)
  // 2. Production flag is true (even if devTools flag is misconfigured)
  if (!isDevToolsEnabled || isProduction) {
    // Log security violation for monitoring
    logger.error(
      '[SECURITY] Attempted access to development route in production:',
      {
        route: sanitizeUrl(state.url),
        isProduction,
        devToolsEnabled: isDevToolsEnabled,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent
      }
    );

    // Redirect to unauthorized page
    router.navigate(['/unauthorized']);
    return false;
  }

  // Allow access in development environment
  return true;
};
